'use client';

/**
 * 组织列表页（v2.0新增）
 * 
 * @页面路径 /organization/organizations
 * @权限要求 organization:read:*
 * @文档参考 docs/modules/organization/05-ui-interaction-spec.md - 页面5: 组织管理页面
 * 
 * 核心功能：
 * - 组织列表展示（卡片+表格）
 * - 搜索功能（组织名称/代码）
 * - 区域筛选、状态筛选
 * - 组织CRUD操作（创建、查看、编辑、删除）
 * - 显示组织统计信息（部门数、员工数）
 */

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useTranslation } from '@/hooks/useTranslation';
import { usePermissions } from '@/hooks/usePermissions';
import { toast } from '@/lib/toast';
import { useConfirm } from '@/components/common/feedback/ConfirmProvider';
import { PageState } from '@/components/common/feedback/PageState';
import { handleApiError } from '@/lib/error-handler';
import {
  Building2,
  Plus,
  Search,
  MapPin,
  Users,
  MoreHorizontal,
  Edit,
  Trash2,
  Eye,
  X,
} from 'lucide-react';
import {
  getOrganizations,
  deleteOrganization,
  getRegions,
  type Organization,
  type Region,
  type OrganizationStatus,
} from '@/services/api/organization';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export default function OrganizationsPage() {
  const router = useRouter();
  const { t } = useTranslation();
  const { hasPermission, isAdmin } = usePermissions();
  const confirm = useConfirm();
  
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [regionFilter, setRegionFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [regions, setRegions] = useState<Region[]>([]);
  const [showMenu, setShowMenu] = useState<string | null>(null);
  const hasSearch = searchTerm.trim().length > 0;
  const emptyTitle = hasSearch
    ? t.common.noMatchingResults
    : t.organization.orgs.list.noOrganizations;
  const emptyDescription = hasSearch ? t.common.tryOtherKeywords : undefined;
  
  // 分页状态
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  

  const canCreate = isAdmin || hasPermission('organization:create');
  const canUpdate = isAdmin || hasPermission('organization:update');
  const canDelete = isAdmin || hasPermission('organization:delete');

  useEffect(() => {
    loadRegions();
  }, []);

  useEffect(() => {
    loadOrganizations();
  }, [page, searchTerm, regionFilter, statusFilter]);

  const loadRegions = async () => {
    try {
      const data = await getRegions();
      setRegions(data || []);
    } catch (err: any) {
      console.error('Failed to load regions:', err);
      // 如果是权限错误,静默处理(区域筛选是可选功能)
      if (err.statusCode === 403) {
        console.warn('User does not have permission to view regions');
        setRegions([]);
      } else {
        // 其他错误也静默处理,不影响主列表加载
        setRegions([]);
      }
    }
  };

  const loadOrganizations = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getOrganizations({
        page,
        limit: pageSize,
        search: searchTerm || undefined,
        regionId: regionFilter || undefined,
        status: (statusFilter as OrganizationStatus) || undefined,
      });
      setOrganizations(data.items || []);
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 1);
    } catch (error: any) {
      handleApiError(error, 'zh', { prefix: t.common.loadFailed });
      setError(t.common.loadFailed);
      setOrganizations([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (org: Organization) => {
    const confirmed = await confirm({
      title: t.organization.orgs.list.confirmDeleteTitle,
      description: t.organization.orgs.list.confirmDeleteMessage.replace('{name}', org.name),
      confirmText: t.common.confirmDelete,
      variant: 'danger',
    });
    if (!confirmed) return;
    try {
      // ✅ 修复：使用正确的删除组织API
      await deleteOrganization(org.id);
      toast.success(t.common.deleteSuccess);
      loadOrganizations();
    } catch (error: any) {
      handleApiError(error, 'zh', { prefix: t.common.deleteFailed });
    }
  };

  // ✅ 修复：搜索已由API处理，无需前端过滤
  const filteredOrgs = organizations;

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: '#f7f8fa' }}>
      {/* 标题栏 */}
      <div className="bg-white border-b" style={{ borderColor: '#e5e6eb' }}>
        <div className="px-6 h-12 flex items-center justify-between">
          <div className="flex items-center gap-4 flex-1">
            <h1 className="text-base font-semibold flex items-center" style={{ color: '#1f2329' }} data-testid="page-title">
              <Building2 className="w-5 h-5 mr-2 text-blue-600" />
              {t.organization.orgs.list.title}
            </h1>
            {/* 搜索框 */}
            <div className="flex-1 max-w-md relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#8f959e' }} data-testid="search-icon" />
              <input
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(1); // 搜索时重置页码
                }}
                placeholder={t.organization.orgs.list.searchPlaceholder}
                className="w-full h-8 pl-9 pr-3 text-sm rounded-md focus:outline-none transition-all"
                style={{ 
                  backgroundColor: '#f7f8fa',
                  border: '1px solid #e5e6eb',
                  color: '#1f2329'
                }}
                data-testid="organization-search-input"
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#3370ff';
                  e.currentTarget.style.backgroundColor = '#ffffff';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = '#e5e6eb';
                  e.currentTarget.style.backgroundColor = '#f7f8fa';
                }}
              />
            </div>

            {/* 区域筛选 */}
            <Select value={regionFilter} onValueChange={(value) => {
              setRegionFilter(value === 'all' ? '' : value);
              setPage(1);
            }}>
              <SelectTrigger className="w-[180px] h-8" data-testid="region-filter">
                <SelectValue placeholder={t.organization.userDetail.statusAll} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t.organization.orgs.list.allRegions}</SelectItem>
                {regions.map((region) => (
                  <SelectItem key={region.id} value={region.id}>
                    {region.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* 状态筛选 */}
            <Select value={statusFilter || 'all'} onValueChange={(value) => {
              setStatusFilter(value === 'all' ? '' : (value as OrganizationStatus));
              setPage(1);
            }}>
              <SelectTrigger className="w-[150px] h-8" data-testid="status-filter">
                <SelectValue placeholder={t.organization.userDetail.statusAll} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t.organization.orgs.list.allStatus}</SelectItem>
                <SelectItem value="ACTIVE">{t.organization.orgs.list.statusActive}</SelectItem>
                <SelectItem value="INACTIVE">{t.organization.orgs.list.statusInactive}</SelectItem>
                <SelectItem value="SUSPENDED">{t.organization.orgs.list.statusSuspended}</SelectItem>
                <SelectItem value="DISSOLVED">{t.organization.orgs.list.statusDissolved}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {/* 新建按钮 */}
          {canCreate && (
            <button
              onClick={() => router.push('/organization/organizations/new')}
              className="flex items-center gap-2 px-4 h-8 rounded-md text-sm font-medium transition-all duration-200"
              style={{
                backgroundColor: '#3370ff',
                color: '#ffffff',
                border: 'none',
                boxShadow: '0 2px 4px rgba(51, 112, 255, 0.2)',
              }}
              data-action="create-organization"
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#0e5aff';
                e.currentTarget.style.boxShadow = '0 4px 8px rgba(51, 112, 255, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#3370ff';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(51, 112, 255, 0.2)';
              }}
            >
              <Plus className="w-4 h-4" />
              {t.organization.orgs.list.createOrg}
            </button>
          )}
        </div>
      </div>

      {/* 内容区 */}
      <div className="flex-1 overflow-auto px-6 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-20" data-testid="loading-skeleton">
            <div className="w-8 h-8 rounded-full animate-spin" style={{ borderTop: '2px solid #3370ff', borderRight: '2px solid transparent' }} />
          </div>
        ) : error ? (
          <div className="py-20 flex items-center justify-center" data-testid="error-state">
            <PageState
              variant="error"
              title={t.organization.orgs.list.loadFailed}
              description={error}
              actionLabel={t.organization.orgs.list.retry}
              onAction={loadOrganizations}
              layout="center"
              className="mx-auto w-full max-w-2xl border-0 bg-transparent"
            />
          </div>
        ) : filteredOrgs.length === 0 ? (
          <div className="py-20 flex items-center justify-center" data-testid="empty-state">
            <PageState
              variant="empty"
              title={emptyTitle}
              description={emptyDescription}
              layout="center"
              className="mx-auto w-full max-w-2xl border-0 bg-transparent"
            />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="organization-table">
            {filteredOrgs.map((org) => {
              return (
                <div
                  key={org.id}
                  className="bg-white rounded-lg p-5 transition-shadow cursor-pointer"
                  style={{ border: '1px solid #e5e6eb' }}
                  onClick={() => router.push(`/organization/organizations/${org.id}`)}
                  data-testid="organization-row"
                  onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)'}
                  onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}
                >
                  {/* 头部 */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#e7f3ff' }}>
                        <Building2 className="w-5 h-5" style={{ color: '#3370ff' }} />
                      </div>
                      <div>
                        <h3 className="font-medium" style={{ color: '#1f2329' }}>{org.name}</h3>
                        {org.code && (
                          <p className="text-sm" style={{ color: '#646a73' }}>{org.code}</p>
                        )}
                      </div>
                    </div>
                    
                    {(canUpdate || canDelete) && (
                      <div className="relative" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => setShowMenu(showMenu === org.id ? null : org.id)}
                          className="p-1.5 rounded-md transition-colors"
                          style={{ 
                            backgroundColor: showMenu === org.id ? '#f7f8fa' : 'transparent'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f7f8fa'}
                          onMouseLeave={(e) => {
                            if (showMenu !== org.id) e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <MoreHorizontal className="w-4 h-4" style={{ color: '#8f959e' }} />
                        </button>
                        
                        {showMenu === org.id && (
                          <div className="absolute right-0 mt-1 w-40 bg-white rounded-lg py-1 z-10" style={{ boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)', border: '1px solid #e5e6eb' }}>
                            <button
                              onClick={() => {
                                router.push(`/organization/organizations/${org.id}`);
                                setShowMenu(null);
                              }}
                              className="w-full px-3 py-2 text-left text-sm flex items-center transition-colors"
                              style={{ color: '#1f2329' }}
                              data-action="view"
                              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f7f8fa'}
                              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            >
                              <Eye className="w-4 h-4 mr-2" />
                              {t.organization.orgs.list.viewDetails}
                            </button>
                            {canUpdate && (
                              <button
                                onClick={() => {
                                  router.push(`/organization/organizations/${org.id}/edit`);
                                  setShowMenu(null);
                                }}
                                className="w-full px-3 py-2 text-left text-sm flex items-center transition-colors"
                                style={{ color: '#1f2329' }}
                                data-action="edit"
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f7f8fa'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                              >
                                <Edit className="w-4 h-4 mr-2" />
                                {t.organization.orgs.list.edit}
                              </button>
                            )}
                            {canDelete && (
                              <button
                                onClick={() => {
                                  handleDelete(org);
                                  setShowMenu(null);
                                }}
                                className="w-full px-3 py-2 text-left text-sm flex items-center transition-colors"
                                style={{ color: '#f53f3f' }}
                                data-action="delete"
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fef0f0'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                {t.organization.orgs.list.delete}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* 描述 */}
                  {org.description && (
                    <p className="text-sm mb-4" style={{ color: '#646a73' }}>
                      {org.description}
                    </p>
                  )}

                  {/* 区域信息 */}
                  {org.organizationRegions && org.organizationRegions.length > 0 && (
                    <div className="mb-3 p-2 rounded-lg" style={{ backgroundColor: '#e7f3ff', border: '1px solid #bdd7ff' }}>
                      <div className="text-xs mb-1" style={{ color: '#646a73' }}>
                        {t.organization.orgs.list.operatingRegions}
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {org.organizationRegions.map((orgRegion) => (
                          <span 
                            key={orgRegion.region.id}
                            className="text-xs px-2 py-0.5 rounded" 
                            style={{ 
                              backgroundColor: orgRegion.isPrimary ? '#3370ff' : '#bdd7ff', 
                              color: orgRegion.isPrimary ? '#ffffff' : '#1f2329'
                            }}
                          >
                            {orgRegion.region.name}
                            {orgRegion.isPrimary && ` (${t.organization.orgs.list.primaryTag})`}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* 统计信息 */}
                  <div className="pt-4 flex items-center gap-4 text-sm" style={{ borderTop: '1px solid #e5e6eb', color: '#646a73' }}>
                    <div className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      <span>{org.organizationRegions?.length || 0} {t.organization.orgs.list.regionCount}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      <span>{org.employeeCount || 0} {t.organization.orgs.list.employeeCount}</span>
                    </div>
                    {org.departmentCount !== undefined && (
                      <div className="flex items-center gap-1">
                        <Building2 className="w-4 h-4" />
                        <span>{org.departmentCount} {t.organization.orgs.list.departmentCount}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 分页组件 */}
      {totalPages > 1 && (
        <div className="bg-white border-t px-6 py-3" style={{ borderColor: '#e5e6eb' }} data-testid="pagination">
          <div className="flex items-center justify-between">
            <div className="text-sm" style={{ color: '#646a73' }}>
              {t.organization.orgs.list.pageInfo
                .replace('{page}', String(page))
                .replace('{totalPages}', String(totalPages))
                .replace('{total}', String(total))}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="px-3 py-1 rounded-md text-sm transition-colors"
                style={{
                  backgroundColor: page === 1 ? '#f7f8fa' : '#fff',
                  border: '1px solid #e5e6eb',
                  color: page === 1 ? '#8f959e' : '#1f2329',
                  cursor: page === 1 ? 'not-allowed' : 'pointer'
                }}
              >
                {t.organization.orgs.list.prevPage}
              </button>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page === totalPages}
                className="px-3 py-1 rounded-md text-sm transition-colors"
                style={{
                  backgroundColor: page === totalPages ? '#f7f8fa' : '#fff',
                  border: '1px solid #e5e6eb',
                  color: page === totalPages ? '#8f959e' : '#1f2329',
                  cursor: page === totalPages ? 'not-allowed' : 'pointer'
                }}
              >
                {t.organization.orgs.list.nextPage}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
